import IMyCourseResponse from "../client/models/myCourse/IMyCourseResponse";
import IBootCampPageModel, {ISectionModel} from "../models/view/bootCampPage/IBootCampPageModel";
import ICard, {iconType} from "../models/view/common/ICard";
import ILesson from "../client/models/myCourse/ILesson";
import {createLessonUrl} from "./lessonPageFactory";
import {DifficultyOption} from "../models/view/common/DifficultyOption";
import IFeaturedBootcampLesson from "../client/models/myLibrary/IFeaturedBootcampLesson";
import {appPagePaths} from "../config/config";
import MyLibraryResponse from "../client/models/myLibrary/MyLibraryResponse";
import ISection from "../client/models/myCourse/ISection";
import {bootcampLastCourseIdRepository} from "../repositories/bootcamp/bootcamp-last-course-id";
import { useParams } from "react-router-dom";

const difficultyNavigationOptions: DifficultyOption[] = ["rookie", "intermediate", "advanced"];
const rookieTourCourseId = 35;
const intermediateTourCourseId = 36;
const advancedTourCourseId = 37;

export function createBootcampUrl(courseId: number){
  return `${appPagePaths.bootCampPage}/${courseId}`;
}

export function getCourseIdFromDifficultyOption(difficultyOption: DifficultyOption){
  switch (difficultyOption){
    case "intermediate": return intermediateTourCourseId;
    case "advanced": return advancedTourCourseId;
    case "rookie":
    default:
      return rookieTourCourseId;
  }
}

export function createBootCampPageViewModel(bootCampCourseResponse: IMyCourseResponse | undefined, courseId: number, currentlySelectedDifficultyNavigationOption: DifficultyOption, myLibraryResponse: MyLibraryResponse, haveBootcampModalsBeenDisplayed: boolean, hasAccessToBootcamp: boolean){
  const featuredBootcampLesson = myLibraryResponse.featuredBootcampLesson;
  const sections = bootCampCourseResponse?.sections?.map(function(s: ISection) {
    return <ISectionModel>{
      id: s.id,
      name: s.name,
      description: s.description,
      cards: s.lessons ? createCardsFromLessons(s.lessons, courseId, featuredBootcampLesson, currentlySelectedDifficultyNavigationOption) : [],
      isComplete: s.isComplete,
      quantityOfNewSongs: s.quantityOfUnlockedSongs,
      courseId: courseId
    };
  }) ?? [];

  const {rookieDescription, intermediateDescription, advancedDescription} = getBootcampDescriptionsFromMyLibraryResponse(myLibraryResponse);
  const bootCampPageViewModel : IBootCampPageModel = {
    hasAccessToBootcamp,
    isLoading: false,
    sections: sections,
    difficultyNavigationOptions: ["rookie", "intermediate", "advanced"],
    currentlySelectedDifficultyNavigationOption,
    featuredLesson: hasAccessToBootcamp ? createFeaturedLesson(featuredBootcampLesson, courseId) : undefined,
    infoModal: {
      isVisible: haveBootcampModalsBeenDisplayed? false : true,
      rookieDescription,
      intermediateDescription,
      advancedDescription,
      isFirstTimeViewed: !haveBootcampModalsBeenDisplayed,
    }
  };
  return bootCampPageViewModel;
}

function markCardsUpNext(cards: ICard[]){
  // let allCardsUpToThisComplete = true;
  //if any have isStarted == true, then we shouldn't mark up next
  if(cards.find(c => c.isStarted)){ return; }
  for(let i = 0; i < cards.length; ++i){
    const card = cards[i];
    if(!card.isComplete){
      card.isUpNext = true;
      break;
    }
  }
}

function getBootcampDescriptionsFromMyLibraryResponse(myLibraryResponse: MyLibraryResponse){
  const descriptions = {rookieDescription: "", intermediateDescription: "", advancedDescription: "" };
  const bootcamp = myLibraryResponse.courseSections.find(cs => cs.name === "Bootcamp");
  if(!bootcamp){ return descriptions; }
  const rookieTourCourse = bootcamp.courses.find(c => c.id === rookieTourCourseId);
  const intermediateTourCourse = bootcamp.courses.find(c => c.id === intermediateTourCourseId);
  const advancedTourCourse = bootcamp.courses.find(c => c.id === advancedTourCourseId);
  if(rookieTourCourse){ descriptions.rookieDescription = rookieTourCourse.description || ""; }
  if(intermediateTourCourse){ descriptions.intermediateDescription = intermediateTourCourse.description || ""; }
  if(advancedTourCourse){ descriptions.advancedDescription = advancedTourCourse.description || ""; }
  return descriptions;
}

export const createInitialBootCampPageViewModel = (): IBootCampPageModel => ({
  hasAccessToBootcamp: true,
  isLoading: true,
  sections: [],
  currentlySelectedDifficultyNavigationOption:"none",
  difficultyNavigationOptions,
  infoModal: {isVisible: false, rookieDescription: "", intermediateDescription: "", advancedDescription: "", isFirstTimeViewed: true}
});

function createFeaturedLesson(featuredBootcampLesson: IFeaturedBootcampLesson, courseId: number){
  const relativeUrl = createLessonUrl(featuredBootcampLesson.lesson.id, featuredBootcampLesson.segmentId, courseId, undefined, appPagePaths.bootCampPage);
  return {
    hashUrl: relativeUrl, //e.g. /lesson/123/324
    lessonName: featuredBootcampLesson.lesson.name, //e.g. 5-Finger Position
    subText: featuredBootcampLesson.lesson.subTitle, //e.g. Rookie Lesson 2
    buttonText: featuredBootcampLesson.buttonText,
    imgUrl: featuredBootcampLesson.lesson.image, //path to cloudfront url
  };
}

function createCardsFromLessons(lessons: ILesson[], courseId: number, featuredBootcampLesson: IFeaturedBootcampLesson, currentlySelectedDifficultyNavigationOption: DifficultyOption){
  const cards = lessons.map((l, index) => createCardFromLesson(l, index+1, courseId, featuredBootcampLesson, currentlySelectedDifficultyNavigationOption));
  markCardsUpNext(cards);
  return cards;
}

function createCardFromLesson(lesson: ILesson, number: number, courseId: number, featuredBootcampLesson: IFeaturedBootcampLesson, currentlySelectedDifficultyNavigationOption: DifficultyOption){
  const segmentId = featuredBootcampLesson.lesson.id === lesson.id ? featuredBootcampLesson.segmentId : undefined; //we want normal card click to go to segment where they left off.
  const bootcampReferrer = createBootcampUrl(courseId);
  const relativeUrl = createLessonUrl(lesson.id, segmentId, courseId, undefined, bootcampReferrer);
  const percentComplete = Math.ceil((lesson.quantityCompleted / lesson.quantitySegments) * 100);
  const amountComplete = lesson.isStarted && !lesson.isComplete ? `${ percentComplete }% complete` : undefined;
  const isHighlighted = featuredBootcampLesson.lesson.id === lesson.id;
  const card: ICard = {
    imageUrl: lesson.image,
    number: number.toString(),
    title: lesson.name,
    subText: lesson.subtext,
    amountComplete,
    icon: lesson.icon as iconType,
    relativeUrl,
    isComplete: lesson.isComplete,
    isStarted: lesson.isStarted && !lesson.isComplete,
    cardType: "bootcamp",
    percentComplete,
    isHighlighted,
    difficultyCategory: currentlySelectedDifficultyNavigationOption,
  };
  return card;
}

/**
 * @deprecated use useBootCampCourseId hook
 * @param locationPath
 */
export function getBootCampCourseIdFromLocationPath(locationPath: string) {
  const parts = locationPath.split("/"); //should result in ["", "bootCamp", "35", ] NOTE: the last part, difficulty, is optional.
  let courseId;
  if(parts.length >= 3){
    const courseIdParsed = parseInt(parts[2]);
    courseId = isNaN(courseIdParsed) ? undefined : courseIdParsed;
  }
  return {courseId};
}

export function useBootCampCourseId(): {courseId: number} {
  const routerParams = useParams();
  const courseIdString = routerParams.difficulty ?? bootcampLastCourseIdRepository.get();
  const courseIdParsed: number = parseInt(courseIdString ?? `${rookieTourCourseId}`, 10);
  const courseId = (isNaN(courseIdParsed) ? undefined : courseIdParsed) ?? rookieTourCourseId;

  return {
    courseId,
  };
}
